import { useEffect, useState, useSyncExternalStore } from 'react'
import { onlineManager, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUserStore } from '@/store/user.ts'
import {
  cachedStageFlag,
  resolveStageFlag,
  type StageFlagT,
} from '@/lib/quickCapture/quickCaptureAccess.ts'

export type StageFlagEnabledState = {
  enabled: boolean
  // True only while nothing is known yet: no cached stage and the fresh read still running.
  isPending: boolean
}

export type StageFlagEnabledOptions = {
  /**
   * One entry to a screen, e.g. its pathname. For a caller that stays mounted while the user moves
   * between screens (the mobile FAB lives in RootLayout), so the read on mount alone would happen
   * once per session. Every new value reads the stage again, and while online the entry stays
   * closed until that read settles, so a cached "on" never flashes for a flag already turned off.
   * Offline the cached flag answers at once. Without it, the cached flag answers at once and the
   * fresh read follows on mount.
   */
  entryKey?: string
  /** False skips every read and reports the entry closed. Defaults to true. */
  enabled?: boolean
}

export type QuickCaptureEnabledState = StageFlagEnabledState
export type QuickCaptureEnabledOptions = StageFlagEnabledOptions

function useIsOnline(): boolean {
  return useSyncExternalStore(
    onStoreChange => onlineManager.subscribe(onStoreChange),
    () => onlineManager.isOnline(),
    () => true,
  )
}

type ScreenEntryT = {
  key: string | null
  generation: number
  settled: boolean
}

/**
 * Whether a stage flag (StageFlagT) is on for the current user. The cached stage answers at once
 * and a fresh read follows on every mount, so a flag turned off elsewhere closes the entry the next
 * time the screen is opened. Offline the cached flag stands (the resolver does not wait for the
 * network). With `entryKey` the fresh read follows every screen entry instead, see
 * StageFlagEnabledOptions. The query is keyed by the flag's name, one family per flag.
 */
export function useStageFlagEnabled(
  flag: StageFlagT,
  projectStageId: string | number,
  options: StageFlagEnabledOptions = {},
): StageFlagEnabledState {
  const queryClient = useQueryClient()
  const userId = useUserStore(state => state.user?.id)
  const isOnline = useIsOnline()
  const active = userId != null && (options.enabled ?? true)

  const query = useQuery({
    queryKey: [flag, { projectStageId: String(projectStageId), userId }],
    queryFn: () => resolveStageFlag(queryClient, flag, projectStageId, userId as number),
    enabled: active,
    // The resolver decides between network and cache itself; a paused query would never ask it.
    networkMode: 'always',
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: () =>
      userId == null ? undefined : cachedStageFlag(queryClient, flag, projectStageId, userId),
  })
  const { refetch } = query

  // A generation per screen entry, derived while rendering rather than in an effect: the render
  // that first shows a new screen already sees its read as outstanding, so no frame shows the
  // answer of the previous entry. Re-entering the same screen is a new generation too.
  const entryKey =
    options.entryKey === undefined
      ? null
      : JSON.stringify([String(projectStageId), userId ?? null, options.entryKey])
  const [screenEntry, setScreenEntry] = useState<ScreenEntryT>({
    key: entryKey,
    generation: 0,
    settled: false,
  })
  if (screenEntry.key !== entryKey) {
    setScreenEntry({ key: entryKey, generation: screenEntry.generation + 1, settled: false })
  }
  const generation = screenEntry.generation
  const settledForEntry = screenEntry.key === entryKey && screenEntry.settled

  useEffect(() => {
    if (entryKey === null || !active) return
    // cancelRefetch: false joins a read already in flight (the one useQuery starts on mount or on
    // a stage change) instead of sending a second request. A read still running from the
    // previous screen is joined too: it was sent moments ago and stacking another one behind it
    // on a slow site would only delay the answer.
    void refetch({ cancelRefetch: false }).then(() => {
      setScreenEntry(current =>
        current.generation === generation ? { ...current, settled: true } : current,
      )
    })
  }, [entryKey, generation, active, refetch])

  const answered = entryKey === null || !isOnline || settledForEntry

  return {
    enabled: active && answered && query.data === true,
    isPending: active && query.data === undefined,
  }
}

/**
 * Whether to show the quick-capture entry on a stage (useStageFlagEnabled over
 * `quickCaptureEnabled`).
 */
export function useQuickCaptureEnabled(
  projectStageId: string | number,
  options: QuickCaptureEnabledOptions = {},
): QuickCaptureEnabledState {
  return useStageFlagEnabled('quickCaptureEnabled', projectStageId, options)
}
